import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { ChevronRight } from '@/components/icons'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { hapticLight } from '@/lib/haptics'
import { OperationsTasksReviewDrawer } from '@/components/operations/OperationsTasksReviewDrawer'

export const Route = createFileRoute('/_auth/tasks/')({
  component: TasksHubRoute,
})

interface TaskHubItem {
  title: string
  subtitle: string
  to?: string
  onClick?: () => void
}

function TasksHubRoute() {
  const { isAdmin } = useIsAdmin()
  const isDesktop = useIsDesktop()
  const [isTasksReviewOpen, setIsTasksReviewOpen] = useState(false)

  const useChecklist: TaskHubItem = {
    title: 'Use Checklist',
    subtitle: 'Fill out or assign to aircraft or crew',
    to: '/tasks/start',
  }

  const items: TaskHubItem[] = isAdmin
    ? [
        {
          title: 'Checklist Builder',
          subtitle: 'Build and manage templates',
          to: '/operations/checklisttemplates',
        },
        useChecklist,
        {
          title: 'Review Tasks',
          subtitle: 'Submitted, active and pending',
          onClick: () => setIsTasksReviewOpen(true),
        },
        {
          title: 'Archived Tasks',
          subtitle: 'Reviewed and read tasks',
          to: '/operations/tasks/archived',
        },
      ]
    : [useChecklist]

  const body = (
    <>
      <OperationsTasksReviewDrawer
        open={isTasksReviewOpen}
        onOpenChange={setIsTasksReviewOpen}
      />

      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const card = (
            <Card className="flex items-center justify-between gap-3 px-4 py-4 active:opacity-60 transition-opacity">
              <div className="flex flex-col gap-1 min-w-0">
                <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
                  {item.title}
                </p>
                <p className="text-sm text-content-primary-subtle leading-5">
                  {item.subtitle}
                </p>
              </div>
              {item.to && (
                <ChevronRight className="size-4 text-content-primary-subtle shrink-0" />
              )}
            </Card>
          )

          if (item.onClick) {
            return (
              <button
                key={item.title}
                type="button"
                className="text-left"
                onClick={() => {
                  hapticLight()
                  item.onClick?.()
                }}
              >
                {card}
              </button>
            )
          }

          return (
            <Link key={item.title} to={item.to} onClick={hapticLight}>
              {card}
            </Link>
          )
        })}
      </div>
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Checklists"
          subtitle="Start, assign and review checklist tasks."
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Checklists" />} className="pt-6">
      {body}
    </AppLayout>
  )
}
